import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTheme, themeStyles } from '../context/ThemeContext';
import ParticleBackground from '../components/ParticleBackground';
import { MoonIcon, SunIcon, ChevronDown, ChevronUp } from 'lucide-react';

const Terms: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const currentTheme = themeStyles[theme];
  
  const sections = [
    { id: 'section1', title: 'الموافقة على الشروط' },
    { id: 'section2', title: 'الحساب واستخدام المنصة' },
    { id: 'section3', title: 'السلوك الممنوع' },
    { id: 'section4', title: 'الملكية الفكرية' },
    { id: 'section5', title: 'خصوصية البيانات' },
    { id: 'section6', title: 'عمليات الدفع' },
    { id: 'section7', title: 'تعديل أو إنهاء الخدمة' },
    { id: 'section8', title: 'القانون المطبق' },
    { id: 'section10', title: 'حدود المسؤولية' },
  ];

  // State to track which accordion sections are open (for mobile view)
  const [openSections, setOpenSections] = useState<{[key: string]: boolean}>({
    section1: true
  });

  const toggleSection = (sectionId: string) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className={`flex flex-col min-h-screen ${currentTheme.background}`}>
      {/* Background particles with reduced opacity */}
      <ParticleBackground />

      {/* ---------- HEADER ---------- */}
      <header className={`fixed top-0 w-full z-50 ${theme === 'dark' ? 'bg-[#1a1a2e]/90' : 'bg-[#F7F9FB]/90'} backdrop-blur-lg shadow-md py-3`}>
        <nav className="max-w-7xl mx-auto px-4 py-1 flex items-center justify-between">
          {/* Left side: brand name */}
          <Link to="/" className={`${currentTheme.text} font-bold text-3xl hover:text-[#4B7BFF] transition-colors`}>
            مسبار
          </Link>

          {/* Right side: theme toggle */}
          <div className="flex items-center">
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-full ${theme === 'dark' ? 'bg-[#2a2a4a] hover:bg-[#3a3a5a] text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'} transition-colors`}
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? <SunIcon className="w-5 h-5" /> : <MoonIcon className="w-5 h-5" />}
            </button>
          </div>
        </nav>
      </header>

      {/* ---------- MAIN CONTENT ---------- */}
      <main className="flex-grow pt-24 pb-16 px-4 dir-rtl">
        <div className="max-w-5xl mx-auto">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className={`text-4xl md:text-5xl font-bold ${currentTheme.text} mb-3`}>
              الشروط والأحكام
            </h1>
            <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} text-lg`}>
              آخر تحديث: ١٩ أبريل ٢٠٢٥
            </p>
          </motion.div>
          
          {/* Desktop View */}
          <div className="hidden md:flex flex-row gap-8">
            {/* Right sidebar */}
            <div className="w-1/4">
              <div className={`sticky top-28 ${theme === 'dark' ? 'bg-[#2a2a4a]/50' : 'bg-white'} p-5 rounded-lg shadow-lg border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-100'}`}>
                <h3 className={`text-xl font-bold ${currentTheme.text} mb-5 text-right border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} pb-3`}>
                  المحتويات
                </h3>
                <ul className="space-y-2 text-right">
                  {sections.map((section, index) => (
                    <li key={section.id} className="mb-1">
                      <button
                        onClick={() => scrollToSection(section.id)}
                        className={`w-full py-2 px-3 text-right rounded-md text-md hover:bg-[#4B7BFF]/10 transition-colors ${currentTheme.text} flex items-center justify-end`}
                      >
                        <span>{section.title}</span>
                        <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${theme === 'dark' ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-600'} ml-2`}>
                          {index + 1}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Main content */}
            <div className="w-3/4">
              <div className={`${theme === 'dark' ? 'bg-[#2a2a4a]/50' : 'bg-white'} p-6 rounded-lg shadow-lg border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-100'}`}>
                <section id="section1" className="mb-10 pb-6 border-b border-dashed border-gray-700/30">
                  <h2 className={`text-2xl font-bold ${currentTheme.text} mb-4 text-right flex items-center justify-end`}>
                    <span>الموافقة على الشروط</span>
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${theme === 'dark' ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-600'} text-center ml-2`}>١</span>
                  </h2>
                  <p className={`${currentTheme.text} text-lg leading-relaxed text-right`}>
                    بمجرد استخدامك لمنصة مسبار، فأنت تقر بموافقتك الكاملة على هذه الشروط والأحكام، وتلتزم بتطبيقها.
                  </p>
                  <p className={`${currentTheme.text} text-lg leading-relaxed mt-3 text-right`}>
                    يحق للإدارة تعديل الشروط في أي وقت حسب الحاجة، ويُعد استمرارك في استخدام المنصة موافقة تلقائية على التعديلات.
                  </p>
                </section>

                <section id="section2" className="mb-10 pb-6 border-b border-dashed border-gray-700/30">
                  <h2 className={`text-2xl font-bold ${currentTheme.text} mb-4 text-right flex items-center justify-end`}>
                    <span>الحساب واستخدام المنصة</span>
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${theme === 'dark' ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-600'} text-center ml-2`}>٢</span>
                  </h2>
                  <div className="text-right">
                    <p className={`${currentTheme.text} text-lg leading-relaxed`}>
                      يجب إدخال بيانات صحيحة عند التسجيل (مثل البريد الإلكتروني ورقم الجوال).
                    </p>
                    <p className={`${currentTheme.text} text-lg leading-relaxed mt-3`}>
                      الحفاظ على سرية اسم المستخدم وكلمة المرور مسؤوليتك، ويُمنع مشاركتها مع أي طرف آخر.
                    </p>
                    <p className={`${currentTheme.text} text-lg leading-relaxed mt-3`}>
                      يحق للإدارة حظر أو تعليق الحساب في حال تم رصد أي نشاط غير مشروع أو مخالف لهذه الشروط دون إشعار مسبق.
                    </p>
                  </div>
                </section>

                <section id="section3" className="mb-10 pb-6 border-b border-dashed border-gray-700/30">
                  <h2 className={`text-2xl font-bold ${currentTheme.text} mb-4 text-right flex items-center justify-end`}>
                    <span>السلوك الممنوع</span>
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${theme === 'dark' ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-600'} text-center ml-2`}>٣</span>
                  </h2>
                  <div className="text-right">
                    <p className={`${currentTheme.text} text-lg leading-relaxed`}>
                      يُمنع استخدام المنصة لأي غرض غير مخصص لها، مثل:
                    </p>
                    <ul className={`${currentTheme.text} text-lg leading-relaxed mt-3 space-y-2 pr-0`}>
                      <li className="flex items-center gap-2 justify-end">
                        <span>محاولات الاختراق أو التلاعب.</span>
                        <span className={`inline-block w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-indigo-400' : 'bg-indigo-500'} flex-shrink-0`}></span>
                      </li>
                      <li className="flex items-center gap-2 justify-end">
                        <span>نشر أو إدخال فيروسات أو محتوى ضار تقنيًا.</span>
                        <span className={`inline-block w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-indigo-400' : 'bg-indigo-500'} flex-shrink-0`}></span>
                      </li>
                      <li className="flex items-center gap-2 justify-end">
                        <span>الاحتيال أو التسبب بالضرر لمستخدمين آخرين.</span>
                        <span className={`inline-block w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-indigo-400' : 'bg-indigo-500'} flex-shrink-0`}></span>
                      </li>
                      <li className="flex items-center gap-2 justify-end">
                        <span>انتحال شخصية أو جهة دون إذن.</span>
                        <span className={`inline-block w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-indigo-400' : 'bg-indigo-500'} flex-shrink-0`}></span>
                      </li>
                    </ul>
                    <p className={`${currentTheme.text} text-lg leading-relaxed mt-4`}>
                      يحق للإدارة اتخاذ جميع الإجراءات القانونية والفنية في حالة المخالفات.
                    </p>
                  </div>
                </section>

                <section id="section4" className="mb-10 pb-6 border-b border-dashed border-gray-700/30">
                  <h2 className={`text-2xl font-bold ${currentTheme.text} mb-4 text-right flex items-center justify-end`}>
                    <span>الملكية الفكرية</span>
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${theme === 'dark' ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-600'} text-center ml-2`}>٤</span>
                  </h2>
                  <div className="text-right">
                    <p className={`${currentTheme.text} text-lg leading-relaxed`}>
                      جميع محتويات المنصة من أسئلة وتصميمات وصور وشعارات وهوية بصرية واسم مسبار هي ملك خاص للموقع.
                    </p>
                    <p className={`${currentTheme.text} text-lg leading-relaxed mt-3`}>
                      لا يجوز نسخ أو نشر أو إعادة استخدام أي من محتويات المنصة أو نقلها إلى مواقع أخرى دون إذن كتابي رسمي.
                    </p>
                  </div>
                </section>

                <section id="section5" className="mb-10 pb-6 border-b border-dashed border-gray-700/30">
                  <h2 className={`text-2xl font-bold ${currentTheme.text} mb-4 text-right flex items-center justify-end`}>
                    <span>خصوصية البيانات</span>
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${theme === 'dark' ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-600'} text-center ml-2`}>٥</span>
                  </h2>
                  <div className="text-right">
                    <p className={`${currentTheme.text} text-lg leading-relaxed`}>
                      نلتزم بالحفاظ على سرية بيانات المستخدم وعدم مشاركتها مع أي طرف خارجي، إلا في الحالات النظامية التي تستدعي الإفصاح لجهات مختصة.
                    </p>
                    <p className={`${currentTheme.text} text-lg leading-relaxed mt-3`}>
                      يتوجب على المستخدم عدم مشاركة بيانات تسجيله مع الآخرين.
                    </p>
                  </div>
                </section>

                <section id="section6" className="mb-10 pb-6 border-b border-dashed border-gray-700/30">
                  <h2 className={`text-2xl font-bold ${currentTheme.text} mb-4 text-right flex items-center justify-end`}>
                    <span>عمليات الدفع</span>
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${theme === 'dark' ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-600'} text-center ml-2`}>٦</span>
                  </h2>
                  <div className="text-right">
                    <p className={`${currentTheme.text} text-lg leading-relaxed`}>
                      تتم عمليات الدفع داخل المنصة عبر بوابات آمنة.
                    </p>
                    <p className={`${currentTheme.text} text-lg leading-relaxed mt-3`}>
                      لا يحتفظ الموقع ببيانات البطاقة كاملة.
                    </p>
                    <p className={`${currentTheme.text} text-lg leading-relaxed mt-3`}>
                      في حالة استخدام بطاقة لا يملكها المستخدم، فإنه يتحمل المسؤولية القانونية كاملة، ويُحظر الحساب نهائيًا في حال ثبوت ذلك.
                    </p>
                    <p className={`${currentTheme.text} text-lg leading-relaxed mt-3`}>
                      جميع المدفوعات للخدمات والاشتراكات غير قابلة للاسترداد أو الاسترجاع تحت أي ظرف من الظروف. بمجرد إتمام عملية الدفع، لا يمكن المطالبة باسترداد المبلغ المدفوع.
                    </p>
                  </div>
                </section>

                <section id="section7" className="mb-10 pb-6 border-b border-dashed border-gray-700/30">
                  <h2 className={`text-2xl font-bold ${currentTheme.text} mb-4 text-right flex items-center justify-end`}>
                    <span>تعديل أو إنهاء الخدمة</span>
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${theme === 'dark' ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-600'} text-center ml-2`}>٧</span>
                  </h2>
                  <div className="text-right">
                    <p className={`${currentTheme.text} text-lg leading-relaxed`}>
                      يحق للإدارة تعديل أي جزء من المنصة أو إيقاف أي خدمة دون إشعار مسبق.
                    </p>
                    <p className={`${currentTheme.text} text-lg leading-relaxed mt-3`}>
                      كما يمكن إنهاء أو تعليق الحسابات التي تخالف الشروط دون الرجوع للمستخدم.
                    </p>
                  </div>
                </section>

                <section id="section8" className="mb-10 pb-6 border-b border-dashed border-gray-700/30">
                  <h2 className={`text-2xl font-bold ${currentTheme.text} mb-4 text-right flex items-center justify-end`}>
                    <span>القانون المطبق</span>
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${theme === 'dark' ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-600'} text-center ml-2`}>٨</span>
                  </h2>
                  <div className="text-right">
                    <p className={`${currentTheme.text} text-lg leading-relaxed`}>
                      تخضع جميع الشروط والأحكام وأنشطة المنصة للقوانين المعمول بها في المملكة العربية السعودية.
                    </p>
                    <p className={`${currentTheme.text} text-lg leading-relaxed mt-3`}>
                      باستخدامك للمنصة، فإنك توافق على الالتزام بهذه القوانين والتعامل معها كمرجع حصري.
                    </p>
                  </div>
                </section>

                <section id="section10" className="mb-4">
                  <h2 className={`text-2xl font-bold ${currentTheme.text} mb-4 text-right flex items-center justify-end`}>
                    <span>حدود المسؤولية</span>
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${theme === 'dark' ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-600'} text-center ml-2`}>٩</span>
                  </h2>
                  <div className="text-right">
                    <p className={`${currentTheme.text} text-lg leading-relaxed`}>
                      منصة مسبار غير مسؤولة عن أي أضرار مباشرة أو غير مباشرة قد تنتج عن استخدام المنصة أو عدم القدرة على استخدامها.
                    </p>
                    <p className={`${currentTheme.text} text-lg leading-relaxed mt-3`}>
                      تقتصر مسؤوليتنا القصوى في جميع الحالات على المبلغ الذي دفعته مقابل الخدمة فقط، ولا نتحمل مسؤولية أي تعويضات إضافية.
                    </p>
                    <p className={`${currentTheme.text} text-lg leading-relaxed mt-3`}>
                      لا نضمن أن المنصة ستكون متاحة بشكل دائم أو خالية من الأخطاء، وقد يتم إيقافها مؤقتًا للصيانة أو التحديث دون إشعار مسبق.
                    </p>
                  </div>
                </section>
              </div>
            </div>
          </div>

          {/* Mobile View - Accordion Style */}
          <div className="md:hidden mt-6">
            <div className={`${theme === 'dark' ? 'bg-[#2a2a4a]/50' : 'bg-white'} rounded-lg shadow-lg border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-100'}`}>
              {sections.map((section, index) => (
                <div key={section.id} className={`border-b ${theme === 'dark' ? 'border-gray-700/50' : 'border-gray-200'} last:border-0`}>
                  <button 
                    className={`w-full py-4 px-5 flex items-center justify-between ${currentTheme.text}`}
                    onClick={() => toggleSection(section.id)}
                    aria-expanded={openSections[section.id]}
                  >
                    <span className={`${theme === 'dark' ? 'text-indigo-300' : 'text-indigo-600'}`}>
                      {openSections[section.id] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </span>
                    <div className="flex items-center">
                      <span className="font-semibold text-right">{section.title}</span>
                      <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${theme === 'dark' ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-600'} mr-2`}>
                        {index + 1}
                      </span>
                    </div>
                  </button>
                  
                  {openSections[section.id] && (
                    <div className={`px-5 pb-5 pt-2 text-right ${currentTheme.text}`}>
                      {section.id === 'section1' && (
                        <div>
                          <p className="text-lg leading-relaxed">
                            بمجرد استخدامك لمنصة مسبار، فأنت تقر بموافقتك الكاملة على هذه الشروط والأحكام، وتلتزم بتطبيقها.
                          </p>
                          <p className="text-lg leading-relaxed mt-3">
                            يحق للإدارة تعديل الشروط في أي وقت حسب الحاجة، ويُعد استمرارك في استخدام المنصة موافقة تلقائية على التعديلات.
                          </p>
                        </div>
                      )}
                      
                      {section.id === 'section2' && (
                        <div>
                          <p className="text-lg leading-relaxed">
                            يجب إدخال بيانات صحيحة عند التسجيل (مثل البريد الإلكتروني ورقم الجوال).
                          </p>
                          <p className="text-lg leading-relaxed mt-3">
                            الحفاظ على سرية اسم المستخدم وكلمة المرور مسؤوليتك، ويُمنع مشاركتها مع أي طرف آخر.
                          </p>
                          <p className="text-lg leading-relaxed mt-3">
                            يحق للإدارة حظر أو تعليق الحساب في حال تم رصد أي نشاط غير مشروع أو مخالف لهذه الشروط دون إشعار مسبق.
                          </p>
                        </div>
                      )}
                      
                      {section.id === 'section3' && (
                        <div>
                          <p className="text-lg leading-relaxed">
                            يُمنع استخدام المنصة لأي غرض غير مخصص لها، مثل:
                          </p>
                          <ul className="text-lg leading-relaxed mt-3 space-y-2">
                            <li className="flex items-center gap-2 justify-end">
                              <span>محاولات الاختراق أو التلاعب.</span>
                              <span className={`inline-block w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-indigo-400' : 'bg-indigo-500'} flex-shrink-0`}></span>
                            </li>
                            <li className="flex items-center gap-2 justify-end">
                              <span>نشر أو إدخال فيروسات أو محتوى ضار تقنيًا.</span>
                              <span className={`inline-block w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-indigo-400' : 'bg-indigo-500'} flex-shrink-0`}></span>
                            </li>
                            <li className="flex items-center gap-2 justify-end">
                              <span>الاحتيال أو التسبب بالضرر لمستخدمين آخرين.</span>
                              <span className={`inline-block w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-indigo-400' : 'bg-indigo-500'} flex-shrink-0`}></span>
                            </li>
                            <li className="flex items-center gap-2 justify-end">
                              <span>انتحال شخصية أو جهة دون إذن.</span>
                              <span className={`inline-block w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-indigo-400' : 'bg-indigo-500'} flex-shrink-0`}></span>
                            </li>
                          </ul>
                          <p className="text-lg leading-relaxed mt-4">
                            يحق للإدارة اتخاذ جميع الإجراءات القانونية والفنية في حالة المخالفات.
                          </p>
                        </div>
                      )}
                      
                      {section.id === 'section4' && (
                        <div>
                          <p className="text-lg leading-relaxed">
                            جميع محتويات المنصة من أسئلة وتصميمات وصور وشعارات وهوية بصرية واسم مسبار هي ملك خاص للموقع.
                          </p>
                          <p className="text-lg leading-relaxed mt-3">
                            لا يجوز نسخ أو نشر أو إعادة استخدام أي من محتويات المنصة أو نقلها إلى مواقع أخرى دون إذن كتابي رسمي.
                          </p>
                        </div>
                      )}
                      
                      {section.id === 'section5' && (
                        <div>
                          <p className="text-lg leading-relaxed">
                            نلتزم بالحفاظ على سرية بيانات المستخدم وعدم مشاركتها مع أي طرف خارجي، إلا في الحالات النظامية التي تستدعي الإفصاح لجهات مختصة.
                          </p>
                          <p className="text-lg leading-relaxed mt-3">
                            يتوجب على المستخدم عدم مشاركة بيانات تسجيله مع الآخرين.
                          </p>
                        </div>
                      )}
                      
                      {section.id === 'section6' && (
                        <div>
                          <p className="text-lg leading-relaxed">
                            تتم عمليات الدفع داخل المنصة عبر بوابات آمنة.
                          </p>
                          <p className="text-lg leading-relaxed mt-3">
                            لا يحتفظ الموقع ببيانات البطاقة كاملة.
                          </p>
                          <p className="text-lg leading-relaxed mt-3">
                            في حالة استخدام بطاقة لا يملكها المستخدم، فإنه يتحمل المسؤولية القانونية كاملة، ويُحظر الحساب نهائيًا في حال ثبوت ذلك.
                          </p>
                          <p className="text-lg leading-relaxed mt-3">
                            جميع المدفوعات للخدمات والاشتراكات غير قابلة للاسترداد أو الاسترجاع تحت أي ظرف من الظروف. بمجرد إتمام عملية الدفع، لا يمكن المطالبة باسترداد المبلغ المدفوع.
                          </p>
                        </div>
                      )}
                      
                      {section.id === 'section7' && (
                        <div>
                          <p className="text-lg leading-relaxed">
                            يحق للإدارة تعديل أي جزء من المنصة أو إيقاف أي خدمة دون إشعار مسبق.
                          </p>
                          <p className="text-lg leading-relaxed mt-3">
                            كما يمكن إنهاء أو تعليق الحسابات التي تخالف الشروط دون الرجوع للمستخدم.
                          </p>
                        </div>
                      )}

                      {section.id === 'section8' && (
                        <div>
                          <p className="text-lg leading-relaxed">
                            تخضع جميع الشروط والأحكام وأنشطة المنصة للقوانين المعمول بها في المملكة العربية السعودية.
                          </p>
                          <p className="text-lg leading-relaxed mt-3">
                            باستخدامك للمنصة، فإنك توافق على الالتزام بهذه القوانين والتعامل معها كمرجع حصري.
                          </p>
                        </div>
                      )}
                      
                      {section.id === 'section10' && (
                        <div>
                          <p className="text-lg leading-relaxed">
                            منصة مسبار غير مسؤولة عن أي أضرار مباشرة أو غير مباشرة قد تنتج عن استخدام المنصة أو عدم القدرة على استخدامها.
                          </p>
                          <p className="text-lg leading-relaxed mt-3">
                            تقتصر مسؤوليتنا القصوى في جميع الحالات على المبلغ الذي دفعته مقابل الخدمة فقط، ولا نتحمل مسؤولية أي تعويضات إضافية.
                          </p>
                          <p className="text-lg leading-relaxed mt-3">
                            لا نضمن أن المنصة ستكون متاحة بشكل دائم أو خالية من الأخطاء، وقد يتم إيقافها مؤقتًا للصيانة أو التحديث دون إشعار مسبق.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* ---------- FOOTER ---------- */}
      <footer className={`mt-auto py-6 ${theme === 'dark' ? 'bg-[#1a1a2e]' : 'bg-[#E8ECEF]'}`}>
        <div className="max-w-5xl mx-auto px-4 text-center">
          <p className={`${currentTheme.text} text-base`}>
            &copy; {new Date().getFullYear()} مسبار. جميع الحقوق محفوظة.
          </p>
          <div className={`mt-2 flex justify-center space-x-4 ${currentTheme.text}`}>
            <Link to="/contact" className="hover:text-[#4B7BFF] transition-colors text-base">
              تواصل معنا
            </Link>
            <Link to="/terms" className="hover:text-[#4B7BFF] transition-colors text-base font-bold">
              الشروط والأحكام
            </Link>
            <Link to="/privacy" className="hover:text-[#4B7BFF] transition-colors text-base">
              سياسة الخصوصية
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Terms; 